import type { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useRef, useState } from 'react';
import {
  ApprovalMode,
  type ApprovalType,
  ApproverFallbackStrategy,
  type Permission,
  type UserRole
} from '@insio/shared-types';
import { ApprovalChainCandidatesDocument, ApprovalOrgUnitsDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useCanChangeAutomationSettings } from '../use-can-change-automation-settings';
import type { ApprovalFlowSectionProps } from './ApprovalFlowSection';
import {
  fallbackDraftOf,
  fallbackInputOf,
  isFallbackSaveable,
  isSameFallback,
  type FallbackDraft,
  type StoredFallback
} from './approval-flow';

const EMPTY_FALLBACK: FallbackDraft = {
  strategy: ApproverFallbackStrategy.ANYONE_WITH_PERMISSION,
  userId: '',
  orgUnitId: '',
  role: '',
  isSelfApprovalForbidden: false,
  isManualApproverSelectionAllowed: false
};

/** The approval settings row every document kind reads and writes. */
export interface ApprovalSettingsRow {
  readonly prompt: string;
  readonly mode: ApprovalMode;
  readonly fallbackApprover: StoredFallback & {
    readonly user?: { readonly id: string; readonly displayName: string } | null;
    readonly eligibleRoles: readonly UserRole[];
    readonly supportedStrategies?: readonly ApproverFallbackStrategy[];
  };
}

interface ApprovalSettingsInput {
  prompt: string;
  mode: ApprovalMode;
  fallbackApprover: ReturnType<typeof fallbackInputOf>;
}

type NoVariables = { [key: string]: never };

export interface ApprovalSettingsFormOptions<TQuery, TUpdate, TReset> {
  settingsQuery: TypedDocumentNode<TQuery, NoVariables>;
  selectSettings: (data: TQuery) => ApprovalSettingsRow;
  updateMutation: TypedDocumentNode<TUpdate, { input: ApprovalSettingsInput }>;
  resetMutation: TypedDocumentNode<TReset, NoVariables>;
  selectReset: (data: TReset) => Pick<ApprovalSettingsRow, 'prompt' | 'mode'>;
  /** The document kind, whose approver candidates the chain may name. */
  approvalType: ApprovalType;
  approvePermission: Permission;
  /** Feature translation prefix holding the kind's save and reset messages. */
  translationPrefix: string;
}

/**
 * State and writes of one kind's approval settings. Only the policy needs a save; the mode and
 * the chain are stored as they change, and a refused write reverts them.
 */
export function useApprovalSettingsForm<TQuery, TUpdate, TReset>({
  settingsQuery,
  selectSettings,
  updateMutation,
  resetMutation,
  selectReset,
  approvalType,
  approvePermission,
  translationPrefix
}: ApprovalSettingsFormOptions<TQuery, TUpdate, TReset>) {
  const t = useFeatureTranslation(translationPrefix);
  const { showSuccess, showError } = useToastHelpers();
  // Whoever only reads the rules sees the settings; no handler below writes for them.
  const isReadOnly = !useCanChangeAutomationSettings();

  const { data, loading, error } = useQuery(settingsQuery);
  // A baseline read, so whoever may change the rules can name people without users.manage.
  const { data: candidatesData, error: candidatesError } = useQuery(
    ApprovalChainCandidatesDocument,
    {
      variables: { approvalType }
    }
  );
  const { data: orgUnitsData } = useQuery(ApprovalOrgUnitsDocument);
  // The responses carry no id to normalise, so the settings query is refetched —
  // otherwise the dirty check keeps comparing against the pre-save snapshot.
  const settingsRefetch = {
    refetchQueries: [{ query: settingsQuery }],
    awaitRefetchQueries: true
  };
  const [updateSettings] = useMutation(updateMutation, settingsRefetch);
  const [resetSettings, { loading: isResetting }] = useMutation(resetMutation, settingsRefetch);

  const [prompt, setPrompt] = useState('');
  // The policy as last stored; an instant write refetches the settings and must not wipe a draft.
  const storedPrompt = useRef<string | null>(null);
  const [mode, setMode] = useState<ApprovalMode>(ApprovalMode.AI_AND_USER);
  const [fallback, setFallback] = useState<FallbackDraft>(EMPTY_FALLBACK);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  // Only the policy save is shown on the button; instant writes never touch its state.
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  const settings = data ? selectSettings(data) : undefined;
  // Active members whose roles grant the approval; undefined until known, so nothing is flagged.
  const candidates = candidatesData?.approverCandidates;
  const users = candidates ?? [];
  const orgUnits = orgUnitsData?.orgUnits ?? [];

  useEffect(() => {
    if (settings) {
      // Read now: the updater may run after the ref below already holds the new policy.
      const previous = storedPrompt.current;
      setPrompt(prev => (previous === null || prev === previous ? settings.prompt : prev));
      storedPrompt.current = settings.prompt;
      setMode(settings.mode);
      setFallback(fallbackDraftOf(settings.fallbackApprover));
    }
  }, [settings]);

  const isPromptDirty = settings != null && prompt !== settings.prompt;

  /** Writes one whole settings row; the policy always travels as stored unless it is being saved. */
  const persist = async (next: {
    prompt: string;
    mode: ApprovalMode;
    fallback: FallbackDraft;
  }): Promise<boolean> => {
    try {
      // errorPolicy 'none' (vs global 'all') makes GraphQL errors throw into the catch below
      await updateSettings({
        variables: {
          input: {
            prompt: next.prompt,
            mode: next.mode,
            fallbackApprover: fallbackInputOf(next.fallback)
          }
        },
        errorPolicy: 'none'
      });
      return true;
    } catch {
      showError(t('saveError'));
      return false;
    }
  };

  /** The chain as stored — a half-edited draft never rides along with another write. */
  const storedFallback = (stored: ApprovalSettingsRow) => fallbackDraftOf(stored.fallbackApprover);

  const handleSavePrompt = async () => {
    if (!settings || isReadOnly) return;
    setIsSavingPrompt(true);
    try {
      if (await persist({ prompt, mode, fallback: storedFallback(settings) })) {
        showSuccess(t('saveSuccess'));
      }
    } finally {
      setIsSavingPrompt(false);
    }
  };

  /** The mode is stored as it flips; a refusal reverts it. */
  const handleModeChange = async (nextMode: ApprovalMode) => {
    if (!settings || isReadOnly) return;
    setMode(nextMode);
    if (
      !(await persist({
        prompt: settings.prompt,
        mode: nextMode,
        fallback: storedFallback(settings)
      }))
    ) {
      setMode(settings.mode);
    }
  };

  /** The chain is stored once it has what it needs; an incomplete one waits, flagged, for its pick. */
  const handleFallbackChange = async (next: FallbackDraft) => {
    // Unchecked against unknown candidates, a chain is never stored; the section locks it too.
    if (candidates === undefined || isReadOnly) return;
    setFallback(next);
    if (!settings) return;
    // A chain naming somebody who may not approve is flagged and waits, like an incomplete one.
    if (
      !isFallbackSaveable(next, {
        users: candidates,
        eligibleRoles: settings.fallbackApprover.eligibleRoles,
        approvePermission
      })
    ) {
      return;
    }
    if (isSameFallback(next, storedFallback(settings))) return;
    if (!(await persist({ prompt: settings.prompt, mode, fallback: next }))) {
      setFallback(storedFallback(settings));
    }
  };

  const handleReset = async () => {
    if (isReadOnly) return;
    try {
      const { data: resetData } = await resetSettings({ errorPolicy: 'none' });
      const restored = resetData ? selectReset(resetData) : undefined;
      if (restored) {
        setPrompt(restored.prompt);
        setMode(restored.mode);
      }
      setIsResetConfirmOpen(false);
      showSuccess(t('resetSuccess'));
    } catch {
      setIsResetConfirmOpen(false);
      showError(t('resetError'));
    }
  };

  /** What `ApprovalFlowSection` takes from the settings; the kind adds its own parts. */
  const flowProps = {
    mode,
    onModeChange: handleModeChange,
    prompt,
    onPromptChange: setPrompt,
    isPromptDirty,
    isSavingPrompt,
    isResetting,
    onSavePrompt: handleSavePrompt,
    onOpenReset: () => {
      if (isReadOnly) return;
      setIsResetConfirmOpen(true);
    },
    fallback,
    onFallbackChange: handleFallbackChange,
    storedFallbackUser: settings?.fallbackApprover.user,
    users: candidates,
    hasCandidatesError: candidates === undefined && candidatesError !== undefined,
    orgUnits,
    roles: settings?.fallbackApprover.eligibleRoles ?? [],
    approvePermission,
    strategies: settings?.fallbackApprover.supportedStrategies,
    isReadOnly
  } satisfies Partial<ApprovalFlowSectionProps>;

  return {
    settings,
    loading,
    error,
    users,
    flowProps,
    isResetting,
    isResetConfirmOpen,
    closeResetConfirm: () => setIsResetConfirmOpen(false),
    handleReset
  };
}
